import {Collection} from '@blesk-frontend/api';
import {FieldErrorMessage, plainTextPreset, Tiptap, titleTextPreset} from '@blesk-frontend/ui';
import {useEffect, useRef} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {toast} from 'sonner';

import {shortDateTimeFormat} from '@/libs';

export interface CollectionFormData extends Pick<Collection, 'name' | 'description'> {}

interface CollectionFormProps {
  collection?: CollectionFormData;
  onSubmit: (data: CollectionFormData) => Promise<void>;
}

export const CollectionForm = ({collection, onSubmit}: CollectionFormProps) => {
  const {
    formState: {errors, isDirty, isValid},
    control,
    handleSubmit,
    watch,
  } = useForm<CollectionFormData>({
    defaultValues: {
      name: `Новая коллекция ${shortDateTimeFormat.format(new Date()).replace(',', '')}`,
      description: '',
      ...collection,
    },
  });

  const {name, description} = watch();
  const previousData = useRef({name, description});

  const onHandleSubmit = () => {
    const promise = handleSubmit(onSubmit)();
    toast.promise(promise, {
      id: 'collection-update',
      loading: 'Сохранение...',
      success: 'Сохранение',
      error: 'Ошибка при сохранении',
    });
  };

  useEffect(() => {
    if (
      !isDirty ||
      !isValid ||
      (previousData.current.name === name && previousData.current.description === description)
    ) {
      return;
    }

    previousData.current = {name, description};

    const timer = setTimeout(() => {
      handleSubmit(onSubmit)();
    }, 1000);

    return () => clearTimeout(timer);
  }, [isDirty, handleSubmit, onSubmit, name, description, isValid]);

  return (
    <>
      <div
        className="mb-4 md:mb-6 [&>.h1]:m-0"
        onBlur={() => {
          onHandleSubmit();
        }}
      >
        <Controller
          control={control}
          name="name"
          rules={{required: 'Обязательное поле'}}
          render={({field}) => (
            <Tiptap
              {...field}
              className="text-text-primary focus-within:bg-bg-secondary-hover hover:bg-bg-secondary-hover"
              preset={titleTextPreset({placeholder: 'Название инструкции'})}
            />
          )}
        />
        <FieldErrorMessage errors={errors} name="name" />
      </div>

      <div
        className="mb-6 whitespace-pre-line leading-[18px] md:mb-8 md:leading-5"
        onBlur={() => {
          onHandleSubmit();
        }}
      >
        <Controller
          control={control}
          name="description"
          render={({field}) => {
            return (
              <Tiptap
                {...field}
                className="text-text-secondary focus-within:bg-bg-secondary-hover hover:bg-bg-secondary-hover"
                preset={plainTextPreset({placeholder: 'Описание инструкции'})}
              />
            );
          }}
        />
      </div>
    </>
  );
};
